/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import clsx from "clsx";
import {UserGroupIcon} from "@heroicons/react/20/solid";
import {consoleLog, currencyNumberFormat} from "utils/utility";
import BookingPackageItemsDurations from "pages/Front/Booking/BookingPackageItems/BookingPackageItemsDurations";
import BookingPackageItemsWithKartingCount, {
    BookingPackageItemsWithKartingCountKartings,
} from "pages/Front/Booking/BookingPackageItems/BookingPackageItemsWithKartingCount";
import {AVAILABLE_DURATIONS_EVENTS, DEFAULT_HALL_ID, IS_UPSELL_ALLOWED} from "helpers/config";
import {PackageCategory} from "types/enums/PackageCategory";
import BookingAction from "store/booking/BookingAction";
import {PackagesItemsPerson} from "types/entities/PackagesItems";
import {usePackageItemKartingOptions} from "utils/packageUtils";
import BookingFooterSteps from "pages/Front/Booking/components/BookingFooterSteps";
import useNavigation, {RouteName} from "utils/hooks/useNavigation";
import useCustomTimeslotsNew from "utils/hooks/useCustomTimeslotsNew";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import {UserIcon} from "@heroicons/react/24/outline";
import Skeleton from "react-loading-skeleton";

export interface BookingPackagesEventParams {}

interface BookingPackagesEventProps extends RouteComponentProps<BookingPackagesEventParams> {}

const BookingPackagesEvent = (props: BookingPackagesEventProps) => {
    const {t} = useTranslation();
    const bookingState = useTypedSelector(state => state.bookingState);
    const {historyPush} = useNavigation();
    const dispatch = useDispatch();
    const availablePackageItems = useTypedSelector(state => state.bookingState.bookingProcess.availablePackageItems);
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const selectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const settings = useTypedSelector(state => state.settingsState);
    const [isKartingDisabled, setIsKartingDisabled] = useState<boolean>(!!settings?.config?.disable_karting_tickets || false);

    const scrollRefTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefCustomTimeslots = useRef<HTMLDivElement>(null);
    const scrollRefConfirmTimeslots = useRef<HTMLDivElement>(null);
    const {bookWithAutomaticTimeslot} = useCustomTimeslotsNew(() => {}, props.history, [
        scrollRefTimeslots,
        scrollRefCustomTimeslots,
        scrollRefConfirmTimeslots,
    ]);
    const [isKartingAvailable, setIsKartingAvailable] = useState<boolean>(false);
    const [chosenDuration, setChosenDuration] = useState<number | undefined>(240);
    const [karting, setKarting] = useState<BookingPackageItemsWithKartingCountKartings>({
        withKarting: 0,
        withoutKarting: 0,
    });
    const personsCount: number = filter.adults + filter.children;
    const [allFilled, setAllFilled] = useState<boolean>(false);
    const [totalPrice, setTotalPrice] = useState<number>(0);
    const [defaultPackageWithKarting, setDefaultPackageWithKarting] = useState<PackagesItemsPerson | null>(null);
    const [defaultPackageWithoutKarting, setDefaultPackageWithoutKarting] = useState<PackagesItemsPerson | null>(null);
    const {setPackageItemHandler, getLeastPossibleDuration, getPackageItemForCombination} = usePackageItemKartingOptions();

    const onNextStepHandler = () => {
        const availablePackages = bookingState?.bookingProcess?.availablePackageItems;
        const availablePackagesHasUpsell = availablePackages ? availablePackages.upsells.length > 0 : false;
        if (IS_UPSELL_ALLOWED && availablePackagesHasUpsell) {
            return historyPush(RouteName.UpsellsNew, "Packages");
        }
        bookWithAutomaticTimeslot(null, true);
    };

    /**
     * Fetches packages based on category and time slot.
     */
    const fetchPackagesByCategoryAndTimeSlot = () => {
        dispatch(
            BookingAction.fetchPackagesByCategoryAndTimeSlot({
                adults: filter.adults,
                children: filter.children,
                date: filter.date,
                package_category_name: filter?.category ? filter.category : PackageCategory.General,
                hall_id: DEFAULT_HALL_ID,
                activity_ids: filter.activity_ids.length === 0 ? [1] : filter.activity_ids,
                timeslot_id: selectedTimeslot ? selectedTimeslot.id : null,
            })
        ).then(r => {
            consoleLog("fetchPackagesByCategoryAndTimeSlot: res", r);
        });
    };

    const setDefaultPackages = () => {
        if (availablePackageItems) {
            const packageWithKarting = availablePackageItems?.packageItems?.adult?.find(item => item.duration === chosenDuration && item.karting);
            setDefaultPackageWithKarting(packageWithKarting ? packageWithKarting : null);
            const packageWithoutKarting = availablePackageItems?.packageItems?.adult?.find(item => item.duration === chosenDuration && !item.karting);
            setDefaultPackageWithoutKarting(packageWithoutKarting ? packageWithoutKarting : null);
        }
    };

    const setTotalPriceHandler = () => {
        if (!chosenDuration) {
            setTotalPrice(0);
        }

        const kartingPrice = defaultPackageWithKarting ? karting.withKarting * defaultPackageWithKarting.price : 0;
        const withoutKartingPrice = defaultPackageWithoutKarting ? karting.withoutKarting * defaultPackageWithoutKarting.price : 0;
        setTotalPrice(kartingPrice + withoutKartingPrice);
        setAllFilled(
            !!(chosenDuration && personsCount === karting.withoutKarting + karting.withKarting && (defaultPackageWithKarting || defaultPackageWithoutKarting))
        );
    };

    const setSelectedPackageWithCount = () => {
        if (defaultPackageWithKarting) {
            setPackageItemHandler(0, defaultPackageWithKarting.id, defaultPackageWithKarting?.person_type, karting.withKarting);
        }
        if (defaultPackageWithoutKarting) {
            setPackageItemHandler(1, defaultPackageWithoutKarting.id, defaultPackageWithoutKarting?.person_type, karting.withoutKarting);
        }
        if (!defaultPackageWithKarting && !defaultPackageWithoutKarting) {
            dispatch(BookingAction.resetSelectedPackageItems());
        }
    };

    const findClosestPackageItemDuration = (newDuration: number | undefined, newKartingState: number) => {
        let availableDurations = AVAILABLE_DURATIONS_EVENTS;
        if (newDuration !== undefined && availableDurations.includes(newDuration)) {
            availableDurations = [
                newDuration,
                ...availableDurations.filter((duration) => duration !== newDuration),
            ];
        }
        for (const duration of availableDurations) {
            const packageItem = getPackageItemForCombination(duration, newKartingState == 1, PackageItemPersonType.Adult);
            if (packageItem) {
                return duration;
            }
        }

        return undefined;
    }

    const isAnyPackageItemWithKartingStateAvailable = (newKartingState?: number | undefined) => {
        for (const duration of AVAILABLE_DURATIONS_EVENTS) {
            const packageItem = getPackageItemForCombination(duration, newKartingState === undefined ? true : newKartingState === 1, PackageItemPersonType.Adult);
            if (packageItem) {
                return true;
            }
        }
        return false;
    }

    useEffect(() => {
        fetchPackagesByCategoryAndTimeSlot();
    }, [filter.adults, filter.children]);

    useEffect(() => {
        setSelectedPackageWithCount();
        setTotalPriceHandler();
    }, [defaultPackageWithKarting, defaultPackageWithoutKarting, chosenDuration, karting]);

    useEffect(() => {
        setDefaultPackages();
    }, [availablePackageItems, chosenDuration, karting]);

    useEffect(() => {
        if (availablePackageItems) {
            const isKartingAvailableCheck = isAnyPackageItemWithKartingStateAvailable();
            setIsKartingAvailable(isKartingAvailableCheck);
            setKarting({
                withKarting: isKartingDisabled || !isKartingAvailableCheck ? 0 : personsCount,
                withoutKarting: isKartingDisabled || !isKartingAvailableCheck ? personsCount : 0
            });
        }
    }, [personsCount, isKartingDisabled, availablePackageItems]);

    useEffect(() => {
        if (availablePackageItems) {
            const currentKarting = isKartingDisabled || !isKartingAvailable ? 0 : personsCount
            const closestDuration = findClosestPackageItemDuration(chosenDuration, currentKarting > 0 ? 1 : 0);
            setChosenDuration(closestDuration)
        }
    }, [isKartingAvailable, availablePackageItems, isKartingDisabled]);

    useEffect(() => {
        setIsKartingDisabled(!!settings?.config?.disable_karting_tickets || false);
    }, [settings?.config?.disable_karting_tickets]);

    if (!availablePackageItems) {
        return (
            <div className={"BookingPackagesEvent w-full"}>
                <div className={"border rounded-md lg:px-1 lg:py-1 xl:px-4 xl:py-3 bg-white shadow"}>
                    <table className="BookingPackagesBasicPackagesPersons w-full lg:border-separate lg:border lg:border-spacing-y-2 lg:border-spacing-x-2 lg:border-white">
                        <colgroup className="hidden lg:table-column-group">
                            <col className="w-auto" />
                            <col className="w-auto" />
                            <col className="w-auto" />
                            <col className="min-w-[110px] w-auto" />
                        </colgroup>
                        <thead className="hidden lg:table-header-group">
                            <tr>
                                <th className="text-left font-bold text-lg text-black"></th>
                                <th className="font-bold text-lg text-black">Optionen</th>
                                <th className="font-bold text-lg text-black">Wähle die Besuchsdauer</th>
                                <th className="font-bold text-lg text-black">Preis</th>
                            </tr>
                        </thead>
                        <tbody className="flex flex-col lg:table-row-group">
                            <tr className="BookingPackagesNewDriverRow flex flex-col lg:table-row last-of-type:border-b-0 border-b lg:border-b-0 px-2 pt-3 pb-2 sm:w-auto sm:px-4 sm:py-4 lg:px-0 lg:py-0">
                                <td className="align-middle py-2 lg:pr-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell border-b border-dashed sm:border-b-0">
                                    <div className="flex flex-row items-center justify-start mb-2 sm:mb-0">
                                        <UserIcon className={clsx("h-7 w-7 xl:h-8 xl:w-8 flex-none mr-2")} />
                                        <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                                        <span className="font-bold text-sm xl:text-base">1. Person</span>
                                    </div>
                                </td>
                                <td className="align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell">
                                    <h4 className="lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]">
                                        Optionen<span>:</span>
                                    </h4>
                                    <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                                </td>
                                <td className="align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell">
                                    <h4 className="lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]">
                                        Ticket nach Stunden<span>:</span>
                                    </h4>
                                    <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                                </td>
                                <td className="align-middle py-2 lg:pl-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell">
                                    <h4 className="lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[170px]">
                                        Preis<span>:</span>
                                    </h4>
                                    <Skeleton className={"w-24 sm:w-48"} height="1.5rem" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
    return (
        <div className={"BookingPackagesEvent w-full"}>
            <div className={"border rounded-md lg:px-1 lg:py-1 xl:px-4 xl:py-3 bg-white shadow"}>
                <table
                    className={
                        "BookingPackagesEventBasicPackagesPersons w-full lg:border-separate lg:border lg:border-spacing-y-2 lg:border-spacing-x-2 lg:border-white"
                    }
                >
                    <colgroup className={"hidden lg:table-column-group"}>
                        <col className="w-auto" />
                        <col className="w-auto" />
                        <col className="w-auto" />
                        <col className="min-w-[110px] w-auto" />
                    </colgroup>
                    <thead className={"hidden lg:table-header-group"}>
                        <tr>
                            <th className={"text-left font-bold text-lg text-black"}></th>
                            <th className={"font-bold text-lg text-black"}>{t("Options")}</th>
                            <th className={"font-bold text-lg text-black"}>{t("Select visit duration")}</th>
                            <th className={"font-bold text-lg text-black"}>{t("Price")}</th>
                        </tr>
                    </thead>
                    <tbody className={"flex flex-col lg:table-row-group"}>
                        <tr
                            className={
                                // "BookingPackagesEventBasicDriverRow flex flex-col lg:table-row border-b lg:border-b-0 px-2 pt-3 pb-2 sm:w-auto sm:px-4 sm:py-4 lg:px-0 lg:py-0"
                                "BookingPackagesEventBasicDriverRow flex flex-col lg:table-row border-b-0 px-2 pt-3 pb-2 sm:w-auto sm:px-4 sm:py-4 lg:px-0 lg:py-0"
                            }
                        >
                            <td
                                className={
                                    "align-middle py-2 lg:pr-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell border-b border-dashed sm:border-b-0"
                                }
                            >
                                <div className={"flex flex-row items-center justify-start mb-2 sm:mb-0"}>
                                    <UserGroupIcon className={clsx("h-7 w-7 xl:h-8 xl:w-8 flex-none mr-2", "text-teal-500")} />
                                    <div className={"flex flex-col items-center"}>
                                        <span className={"font-bold text-sm xl:text-base"}>{t("All persons")}</span>
                                    </div>
                                </div>
                            </td>
                            <td
                                className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row md: items-center lg:table-cell"}
                            >
                                <h4 className={"lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[210px]"}>
                                    {t("Ticket by hours")}
                                    <span>:</span>
                                </h4>
                                <BookingPackageItemsWithKartingCount
                                    className={"flex-row items-center justify-center sm:justify-start lg:justify-center"}
                                    karting={karting}
                                    personCount={personsCount}
                                    setKarting={setKarting}
                                    isKartingDisable={isKartingDisabled || !isKartingAvailable}
                                />
                            </td>
                            <td className={"align-middle py-2 lg:px-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell"}>
                                <h4 className={"lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[210px]"}>
                                    {t("Select visit duration")}
                                    <span>:</span>
                                </h4>
                                <BookingPackageItemsDurations
                                    disabled1Hour={true}
                                    disabled2Hours={filter.category === PackageCategory.EventS || filter.category === PackageCategory.Kinder}
                                    chosenDuration={chosenDuration}
                                    className={"justify-start lg:justify-center"}
                                    enabled={true}
                                    personType={PackageItemPersonType.Adult}
                                    setChosenDuration={setChosenDuration}
                                />
                            </td>
                            <td className={"align-middle py-2 lg:pl-2 lg:py-1 w-full lg:w-auto inline-flex flex-col sm:flex-row items-center lg:table-cell"}>
                                <h4 className={"lg:hidden font-bold text-base text-black mb-2 sm:mb-0 sm:mr-4 shrink-0 sm:min-w-[210px]"}>
                                    {t("Price")}
                                    <span>:</span>
                                </h4>
                                <div
                                    className={clsx(
                                        "flex flex-row items-center justify-center font-medium text-gray-700 text-sm xl:text-base",
                                        totalPrice && "underline underline-offset-4 lg:underline-offset-8"
                                    )}
                                >
                                    {totalPrice ? <span>{currencyNumberFormat(totalPrice)}</span> : <span>---</span>}
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className={"flex flex-col items-end justify-center w-full mt-6"}>
                <div
                    className={
                        "flex flex-col items-center justify-center rounded-md bg-white text-sm font-semibold border shadow w-full sm:min-w-[210px] sm:w-auto"
                    }
                >
                    <h3 className={"font-bold text-xl text-black w-full px-4 py-3 border-b border-dashed flex justify-center sm:justify-end"}>
                        {t("Total price")}
                    </h3>
                    <div className={"text-base font-bold text-gray-600 w-full px-4 py-3 flex justify-center sm:justify-end"}>
                        {totalPrice ? <span>{currencyNumberFormat(totalPrice)}</span> : <span>---</span>}
                    </div>
                    <div className={"flex flex-col text-center sm:text-right text-xs text-gray-500 w-full px-4 pb-3"}>
                        {defaultPackageWithoutKarting?.price && (
                            <span>
                                {t("without Karting")}: {currencyNumberFormat(defaultPackageWithoutKarting?.price)} {t("p.P.")}
                            </span>
                        )}
                        {defaultPackageWithKarting?.price && (
                            <span>
                                {t("with Karting")}: {currencyNumberFormat(defaultPackageWithKarting?.price)} {t("p.P.")}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <BookingFooterSteps className={"mt-6"} iconName={"card"} nextStepDisabled={!allFilled} onClick={() => onNextStepHandler()} />
        </div>
    );
};

export default withRouter(BookingPackagesEvent);
